import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND } from "@/lib/constants";
import logoImg from "@/logo.png";
import logoTextImg from "@/logo-text.png";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (
    loc.pathname.startsWith('/invite') ||
    loc.pathname.startsWith('/photos') ||
    loc.pathname.startsWith('/admin') ||
    loc.pathname.startsWith('/ar')
  ) {
    return null;
  }

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "backdrop-blur-xl bg-ink/70 border-b border-line" : "bg-transparent"
        }`}
        data-testid="main-nav"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img src={logoImg} alt="KV Eventech Logo" className="h-10 w-auto object-contain" />
            <img src={logoTextImg} alt="KV Eventech Text" className="h-4 w-auto object-contain hidden sm:block" />
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-display text-[11px] uppercase tracking-[0.28em] link-underline transition-colors ${
                    isActive ? "text-copper" : "text-parchment/70 hover:text-parchment"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link
              to="/contact"
              data-testid="nav-cta-book"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-[11px] uppercase tracking-[0.28em] font-display bg-copper text-ink hover:bg-copperHover transition-colors"
            >
              Book a Date
            </Link>
          </div>

          <button
            className="lg:hidden text-parchment"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-ink/98 lg:hidden flex flex-col items-center justify-center"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col items-center gap-10">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.05 + i * 0.08 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `font-serif text-5xl ${isActive ? "text-copper" : "text-parchment"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="mt-6 text-center">
                <div className="text-xs uppercase tracking-[0.3em] text-copper font-display mb-2">Get in Touch</div>
                <a href={`tel:${BRAND.phone}`} className="text-parchment font-serif text-2xl">{BRAND.phoneDisplay}</a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
